import { useRef, useMemo, useContext } from "react";
import { Autocomplete, TextField, IconButton, Paper } from "@mui/material";
import { ReactComponent as DeleteIcon } from "images/svg/trash.svg";
import styles from "./styles.module.css";
import { roundOff, amountExtendedRoundOff, amountRoundOff } from "helpers/NumberFormatHelper";
import { customFilterOptions } from "helpers/SearchHelper";
import TaxHelper from "helpers/TaxHelper";
import ShopContext from "context/ShopContext";
import { usePermission } from "hooks/usePermission";
import EditableTextField from "components/utils/EditableTextField/EditableTextField";
import { KEYBOARD_KEYS, TAX_PREFERENCES, STAFF_PERMISSION } from "const";
import { ReactComponent as AddIcon } from "images/svg/add-circle.svg";
import QtyTextField from "components/utils/QtyTextField/QtyTextField";
import { isScanningBarcode } from "helpers/BarcodeHelper";

const DropdownPaper = (props) => {
  const { sx, ...otherProps } = props;
  return (
    <Paper
      {...otherProps}
      sx={{
        ...sx,
        "& .MuiAutocomplete-option.Mui-focused": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true']": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option[data-focus='true']": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
        "& .MuiAutocomplete-option:hover": {
          backgroundColor: "#E50218 !important",
          color: "white !important",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          padding: "8px 16px",
          fontWeight: "bold",
          borderBottom: "1px solid #e0e0e0",
          color: "#E50218",
          fontSize: "13px",
        }}
      >
        <div style={{ width: "60%" }}>Item</div>
        <div style={{ width: "20%", textAlign: "right" }}>MRP</div>
        <div style={{ width: "20%", textAlign: "right" }}>Price</div>
      </div>
      {props.children}
    </Paper>
  );
};

function ERPTable({
  saleItems,
  items,
  handleUpdateRowProduct,
  handleUpdateRowQuantity,
  handleUpdateRowDiscount,
  selectedProduct,
  setSelectedProduct,
  productSearchText,
  setProductSearchText,
  quantity,
  setQuantity,
  itemDiscountPercentage,
  handleUpdateItemDiscount,
  itemTaxPercentage,
  itemRate,
  handleUpdateItemRate,
  handleUpdateRowRate,
  handleAddSelectedProduct,
  productInputRef,
  amountReceivedRef,
  keyPadRef,
  onKeypadFocus,
  handleRemoveItemFromSale,
}) {
  const { selectedShop } = useContext(ShopContext);
  const { checkPermissionAndExecute, PermissionDialog } = usePermission();

  const quantityRef = useRef(null);

  const handleProductKeyDown = (e) => {
    if (isScanningBarcode() || e.defaultPrevented || e.isHandled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === KEYBOARD_KEYS.ENTER) {
      e.preventDefault();
      if (selectedProduct) {
        setTimeout(() => quantityRef.current?.focus(), 100);
      } else {
        const inputValue = productSearchText || productInputRef.current?.value || "";
        if (!inputValue.trim()) {
          if (keyPadRef?.current) {
            keyPadRef.current.focus();
            if (onKeypadFocus) onKeypadFocus();
          } else {
            amountReceivedRef.current?.focus();
          }
        }
      }
    }
  };

  const handleEnterKeyDown = (e) => {
    if (isScanningBarcode() || e.defaultPrevented || e.isHandled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === KEYBOARD_KEYS.ENTER) {
      e.preventDefault();
      handleAddSelectedProduct();
    }
  };

  const handleSecureFieldClick = (e, type) => {
    const target = e.target;
    target.blur();

    checkPermissionAndExecute(
      type,
      () => {
        target.focus();
        target.select();
      },
      type === STAFF_PERMISSION.PRICE,
    );
  };

  const totals = useMemo(() => {
    return saleItems.reduce(
      (acc, item) => {
        const qty = Number(item.qty) || 0;
        const rate = Number(item.rate) || 0;
        const payable = Number(item.payable) || 0;
        const taxAmount = Number(item.tax) || 0;
        const discountAmount = Number(item.discount) || 0;

        acc.qty += qty;
        acc.rate += rate * qty;
        acc.discount += discountAmount;
        acc.tax += (taxAmount > 0 ? taxAmount : 0) + (Number(item.cess) || 0);
        acc.grandTotal += payable;
        return acc;
      },
      { qty: 0, rate: 0, discount: 0, tax: 0, grandTotal: 0 },
    );
  }, [saleItems]);

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.tableContainer}>
        <table className={styles.productTable}>
          <thead>
            <tr>
              <th style={{ width: "60px", borderTopLeftRadius: "10px" }}>Sl No</th>
              <th>Product</th>
              <th style={{ width: "85px" }}>Qty</th>
              <th style={{ width: "85px" }}>Rate</th>
              <th style={{ width: "85px" }}>Disc %</th>
              {selectedShop?.tax_preference !== TAX_PREFERENCES.NO_TAX && <th style={{ width: "85px" }}>Tax Amt</th>}
              <th style={{ width: "100px" }}>Total</th>
              <th style={{ width: "60px", borderTopRightRadius: "10px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {saleItems.map((row, index) => (
              <tr key={row.cartId || index}>
                <td>{index + 1}</td>
                <td style={{ padding: 0 }}>
                  <Autocomplete
                    options={items}
                    getOptionLabel={(option) => option.name}
                    filterOptions={customFilterOptions}
                    value={items.find((i) => i.id === row.productId) || null}
                    onChange={(e, val) => handleUpdateRowProduct(row, val)}
                    disableClearable
                    autoHighlight
                    PaperComponent={DropdownPaper}
                    renderOption={(props, option, state) => {
                      return (
                        <li
                          {...props}
                          key={`${option.id}-${state.index}`}
                          style={{ ...props.style, fontSize: "13px", display: "flex", width: "100%" }}
                        >
                          <div
                            style={{ width: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {option.name}
                          </div>
                          <div style={{ width: "20%", textAlign: "right" }}>
                            {option.mrp !== undefined && option.mrp !== null ? roundOff(option.mrp) : "-"}
                          </div>
                          <div style={{ width: "20%", textAlign: "right" }}>
                            {option.price !== undefined && option.price !== null ? roundOff(option.price) : "-"}
                          </div>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                          width: "100%",
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-popupIndicator": {
                        visibility: "hidden",
                      },
                      "&.Mui-focused .MuiAutocomplete-popupIndicator": {
                        visibility: "visible",
                      },
                      "&:hover .MuiAutocomplete-popupIndicator": {
                        visibility: "visible",
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "13px",
                        padding: "8px 12px !important",
                      },
                      width: "100%",
                    }}
                  />
                </td>
                <td style={{ padding: 0 }}>
                  <QtyTextField
                    value={amountExtendedRoundOff(row.qty)}
                    onCommit={(val) => handleUpdateRowQuantity(row, val)}
                    sx={{
                      "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                      width: "100%",
                    }}
                  />
                </td>
                <td style={{ padding: 0 }}>
                  <EditableTextField
                    externalValue={row.rate}
                    onCommit={(val) => handleUpdateRowRate(row, val)}
                    onClick={(e) => handleSecureFieldClick(e, "price")}
                    sx={{
                      "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                      width: "100%",
                    }}
                  />
                </td>
                <td style={{ padding: 0 }}>
                  <EditableTextField
                    externalValue={row.discountPercentage || 0}
                    onCommit={(val) => handleUpdateRowDiscount(row, val)}
                    onClick={(e) => handleSecureFieldClick(e, "discount")}
                    sx={{
                      "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                      width: "100%",
                    }}
                  />
                </td>
                {selectedShop?.tax_preference !== TAX_PREFERENCES.NO_TAX && (
                  <td>{roundOff((row.tax || 0) + (row.cess || 0))}</td>
                )}

                <td>{roundOff(row.payable)}</td>
                <td>
                  <IconButton onClick={() => handleRemoveItemFromSale(row.localId)}>
                    <DeleteIcon sx={{ color: "#E50218", fontSize: "18px" }} />
                  </IconButton>
                </td>
              </tr>
            ))}

            {/* Input Row */}
            <tr className={styles.inputRow} key="erp-input-row">
              <td>{saleItems.length + 1}</td>
              <td style={{ padding: 0 }}>
                <Autocomplete
                  options={items}
                  getOptionLabel={(option) => option.name}
                  filterOptions={customFilterOptions}
                  value={selectedProduct}
                  inputValue={productSearchText || ""}
                  onInputChange={(e, val) => {
                    if (isScanningBarcode()) {
                      return;
                    }
                    if (setProductSearchText) {
                      setProductSearchText(val);
                    }
                  }}
                  onChange={(e, val) => {
                    if (isScanningBarcode()) {
                      return;
                    }
                    setSelectedProduct(val);
                    if (val) {
                      setTimeout(() => quantityRef.current?.focus(), 100);
                    }
                  }}
                  autoHighlight
                  onKeyDown={(e) => {
                    if (isScanningBarcode() || e.defaultPrevented || e.isHandled) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    handleProductKeyDown(e);
                  }}
                  PaperComponent={DropdownPaper}
                  renderOption={(props, option, state) => (
                    <li
                      {...props}
                      key={`${option.id}-${state.index}`}
                      style={{ ...props.style, fontSize: "13px", display: "flex", width: "100%" }}
                    >
                      <div style={{ width: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {option.name}
                      </div>
                      <div style={{ width: "20%", textAlign: "right" }}>
                        {option.mrp !== undefined && option.mrp !== null ? roundOff(option.mrp) : "-"}
                      </div>
                      <div style={{ width: "20%", textAlign: "right" }}>
                        {option.price !== undefined && option.price !== null ? roundOff(option.price) : "-"}
                      </div>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={productInputRef}
                      placeholder="(F1) Search Product"
                      variant="standard"
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      inputProps={{
                        ...params.inputProps,
                        tabIndex: -1,
                        onKeyDown: (e) => {
                          if (isScanningBarcode() || e.defaultPrevented || e.isHandled) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          params.inputProps?.onKeyDown?.(e);
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                        width: "100%",
                      }}
                    />
                  )}
                />
              </td>
              <td style={{ padding: 0 }}>
                <QtyTextField
                  value={quantity}
                  onChange={(val) => setQuantity(val)}
                  inputRef={quantityRef}
                  onKeyDown={handleEnterKeyDown}
                  disabled={!selectedProduct}
                  placeholder="Qty"
                  sx={{
                    "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                    width: "100%",
                  }}
                />
              </td>
              <td style={{ padding: 0 }}>
                <TextField
                  type="number"
                  variant="standard"
                  onKeyDown={handleEnterKeyDown}
                  InputProps={{ disableUnderline: true }}
                  value={amountExtendedRoundOff(itemRate)}
                  onChange={(e) => handleUpdateItemRate(e.target.value)}
                  disabled={!selectedProduct}
                  placeholder="Rate"
                  onClick={(e) => handleSecureFieldClick(e, "price")}
                  sx={{
                    "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                    width: "100%",
                  }}
                />
              </td>
              <td style={{ padding: 0 }}>
                <TextField
                  type="number"
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  inputProps={{ min: 0, max: 100 }}
                  value={itemDiscountPercentage}
                  onKeyDown={handleEnterKeyDown}
                  onChange={(e) => handleUpdateItemDiscount(e.target.value)}
                  disabled={!selectedProduct}
                  onClick={(e) => handleSecureFieldClick(e, "discount")}
                  sx={{
                    "& .MuiInputBase-input": { fontSize: "13px", padding: "8px 12px !important" },
                    width: "100%",
                  }}
                />
              </td>
              {selectedShop?.tax_preference !== TAX_PREFERENCES.NO_TAX && (
                <td>
                  {selectedProduct && quantity
                    ? roundOff(
                        TaxHelper.getItemTax(itemTaxPercentage, itemRate * quantity) +
                          TaxHelper.getItemCess(
                            selectedProduct.cessPercentage ?? selectedProduct.cess ?? 0,
                            itemRate * quantity,
                          ),
                      )
                    : ""}
                </td>
              )}
              <td></td>
              <td style={{ textAlign: "center" }}>
                <IconButton
                  onClick={handleAddSelectedProduct}
                  disabled={!selectedProduct}
                  sx={{ width: 28, height: 28, padding: "4px" }}
                >
                  <AddIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={styles.tableFooterSection}>
        <table className={styles.productTable}>
          <tfoot>
            <tr>
              <td
                colSpan="2"
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  paddingLeft: "15px",
                  borderBottomLeftRadius: "10px",
                }}
              >
                Total
              </td>
              <td style={{ width: "85px", fontWeight: "bold" }}>{amountRoundOff(totals?.qty || 0)}</td>
              <td style={{ width: "85px", fontWeight: "bold" }}></td>
              <td style={{ width: "85px", fontWeight: "bold" }}>{roundOff(totals?.discount || 0)}</td>
              {selectedShop?.tax_preference !== TAX_PREFERENCES.NO_TAX && (
                <td style={{ width: "85px", fontWeight: "bold" }}>{roundOff(totals?.tax || 0)}</td>
              )}
              <td style={{ fontWeight: "bold", width: "100px" }}>{roundOff(totals?.grandTotal || 0)}</td>
              <td style={{ width: "65px", borderBottomRightRadius: "10px" }}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <PermissionDialog />
    </div>
  );
}

export default ERPTable;
